import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { LogIn, Loader2, ArrowLeft, CreditCard, Eye, AlertCircle, UserPlus, CheckCircle, ArrowRight, Key, Building } from 'lucide-react';
import axios from 'axios';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [subscriptionError, setSubscriptionError] = useState(null);
  
  // Activation state (individual)
  const [showActivation, setShowActivation] = useState(false);
  const [activationStep, setActivationStep] = useState(1); // 1: email, 2: password
  const [activationEmail, setActivationEmail] = useState('');
  const [activationPassword, setActivationPassword] = useState('');
  const [activationPasswordConfirm, setActivationPasswordConfirm] = useState('');
  const [activationLoading, setActivationLoading] = useState(false);
  const [activationSuccess, setActivationSuccess] = useState(null);

  // Code activation state (enterprise)
  const [showCodeActivation, setShowCodeActivation] = useState(false);
  const [codeStep, setCodeStep] = useState(1); // 1: code, 2: email+password
  const [activationCode, setActivationCode] = useState('');
  const [codeInfo, setCodeInfo] = useState(null);
  const [codeEmail, setCodeEmail] = useState('');
  const [codePassword, setCodePassword] = useState('');
  const [codePasswordConfirm, setCodePasswordConfirm] = useState('');
  const [codeLoading, setCodeLoading] = useState(false);
  const [codeSuccess, setCodeSuccess] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubscriptionError(null);
    
    if (!username || !password) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    setLoading(true);
    try {
      const result = await login(username, password);
      
      // Check if subscription is required
      if (result.subscriptionRequired) {
        setSubscriptionError(result.subscription);
        toast.error(result.subscription.reason || 'Abonnement requis');
        return;
      }
      
      toast.success('Connexion réussie');
      
      if (result.must_change_password) {
        navigate('/change-password');
      } else {
        navigate('/search');
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'Identifiants incorrects');
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = () => {
    const subscriptionUrl = subscriptionError?.redirect_url || 'https://soltion-numerique-en-enquete.myshopify.com/collections/plans';
    window.open(subscriptionUrl, '_blank');
  };

  const handleCheckEmail = async (e) => {
    e.preventDefault();
    
    if (!activationEmail) {
      toast.error('Veuillez entrer votre email');
      return;
    }

    setActivationLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/check-subscription?email=${encodeURIComponent(activationEmail)}`
      );
      
      if (response.data.subscription_active) {
        toast.success('Abonnement trouvé! Créez votre mot de passe.');
        setActivationStep(2);
      }
    } catch (error) {
      console.error('Check subscription error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la vérification');
    } finally {
      setActivationLoading(false);
    }
  };

  const handleActivateAccount = async (e) => {
    e.preventDefault();
    
    if (!activationPassword || !activationPasswordConfirm) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    
    if (activationPassword !== activationPasswordConfirm) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    
    if (activationPassword.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    setActivationLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/activate-account`,
        {
          email: activationEmail,
          password: activationPassword
        }
      );
      
      if (response.data.success) {
        setActivationSuccess(response.data);
        toast.success('Compte activé avec succès!');
      }
    } catch (error) {
      console.error('Activation error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'activation');
    } finally {
      setActivationLoading(false);
    }
  };

  const resetActivation = () => {
    setShowActivation(false);
    setActivationStep(1);
    setActivationEmail('');
    setActivationPassword('');
    setActivationPasswordConfirm('');
    setActivationSuccess(null);
  };

  // Code activation functions
  const handleVerifyCode = async (e) => {
    e.preventDefault();
    
    if (!activationCode) {
      toast.error('Veuillez entrer votre code d\'activation');
      return;
    }

    setCodeLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/verify-code?code=${encodeURIComponent(activationCode.toUpperCase())}`
      );
      
      if (response.data.valid) {
        setCodeInfo(response.data);
        toast.success('Code valide! Créez votre compte.');
        setCodeStep(2);
      }
    } catch (error) {
      console.error('Code verification error:', error);
      toast.error(error.response?.data?.detail || 'Code invalide');
    } finally {
      setCodeLoading(false);
    }
  };

  const handleActivateWithCode = async (e) => {
    e.preventDefault();
    
    if (!codeEmail || !codePassword || !codePasswordConfirm) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    
    if (codePassword !== codePasswordConfirm) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    
    if (codePassword.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    setCodeLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/activate-with-code`,
        {
          code: activationCode.toUpperCase(),
          username: codeEmail,
          password: codePassword
        }
      );
      
      if (response.data.success) {
        setCodeSuccess(response.data);
        toast.success('Compte activé avec succès!');
      }
    } catch (error) {
      console.error('Code activation error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'activation');
    } finally {
      setCodeLoading(false);
    }
  };

  const resetCodeActivation = () => {
    setShowCodeActivation(false);
    setCodeStep(1);
    setActivationCode('');
    setCodeInfo(null);
    setCodeEmail('');
    setCodePassword('');
    setCodePasswordConfirm('');
    setCodeSuccess(null);
  };

  // Code Activation Success Screen
  if (codeSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 pt-24 bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950">
        <div className="w-full max-w-md">
          <div className="glass rounded-2xl p-8 fade-in border border-gray-800 text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold mb-2 text-white">Compte activé!</h1>
            <p className="text-gray-400 mb-6">Votre compte a été créé avec succès.</p>
            
            <div className="bg-gray-800/50 rounded-lg p-4 mb-4 text-left space-y-2">
              <p className="text-gray-300 text-sm">
                <span className="text-gray-500">Email (identifiant):</span>{' '}
                <span className="font-mono text-amber-500 font-bold text-base">{codeSuccess.username}</span>
              </p>
              <p className="text-gray-300 text-sm">
                <span className="text-gray-500">Plan:</span>{' '}
                <span className="text-amber-400 capitalize">
                  {codeSuccess.plan?.includes('monthly') ? 'Mensuel' : 
                   codeSuccess.plan?.includes('annual') ? 'Annuel' : 
                   codeSuccess.plan?.includes('demo') ? 'Démo' : codeSuccess.plan}
                </span>
              </p>
              {codeSuccess.license_number && (
                <p className="text-gray-300 text-sm">
                  <span className="text-gray-500">Licence:</span>{' '}
                  <span className="text-gray-300">#{codeSuccess.license_number} / {codeSuccess.total_licenses}</span>
                </p>
              )}
              {codeSuccess.remaining_licenses !== undefined && (
                <p className="text-gray-300 text-sm">
                  <span className="text-gray-500">Licences restantes:</span>{' '}
                  <span className="text-green-400">{codeSuccess.remaining_licenses}</span>
                </p>
              )}
            </div>

            <div className="bg-blue-900/30 border border-blue-700/50 rounded-lg p-3 mb-6 text-left">
              <p className="text-blue-300 text-xs">
                <strong>💡 Pour vous connecter:</strong> Utilisez votre <strong>adresse email complète</strong> comme identifiant.
              </p>
            </div>
            
            <Button
              onClick={() => {
                setUsername(codeSuccess.username);
                resetCodeActivation();
              }}
              className="w-full bg-amber-600 hover:bg-amber-700"
            >
              <LogIn className="w-4 h-4 mr-2" />
              Se connecter maintenant
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Code Activation Form
  if (showCodeActivation) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 pt-24 bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950">
        <div className="w-full max-w-md">
          <Button 
            variant="ghost" 
            onClick={resetCodeActivation}
            className="text-gray-400 hover:text-white mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour à la connexion
          </Button>

          <div className="glass rounded-2xl p-8 fade-in border border-gray-800">
            <div className="text-center mb-8">
              <Key className="w-12 h-12 text-amber-500 mx-auto mb-4" />
              <h1 className="text-2xl font-bold mb-2 text-white">
                Activer avec un code
              </h1>
              <p className="text-gray-400">
                {codeStep === 1 
                  ? "Entrez votre code d'activation"
                  : "Créez votre compte"
                }
              </p>
            </div>

            {/* Progress Steps */}
            <div className="flex items-center justify-center gap-2 mb-6">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                codeStep >= 1 ? 'bg-amber-600 text-white' : 'bg-gray-700 text-gray-400'
              }`}>1</div>
              <div className={`w-12 h-1 ${codeStep >= 2 ? 'bg-amber-600' : 'bg-gray-700'}`}></div>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                codeStep >= 2 ? 'bg-amber-600 text-white' : 'bg-gray-700 text-gray-400'
              }`}>2</div>
            </div>

            {codeStep === 1 ? (
              <form onSubmit={handleVerifyCode} className="space-y-4">
                <div>
                  <Label htmlFor="activation-code" className="text-gray-300">Code d'activation</Label>
                  <Input
                    id="activation-code"
                    type="text"
                    value={activationCode}
                    onChange={(e) => setActivationCode(e.target.value)}
                    className="bg-gray-900/50 border-gray-700 text-white font-mono text-center text-lg tracking-widest"
                    placeholder="Numéro de commande Shopify"
                    disabled={codeLoading}
                  />
                  <p className="text-xs text-gray-500 mt-1 text-center">
                    Entrez le numéro reçu dans votre email de confirmation
                  </p>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-amber-600 hover:bg-amber-700 h-12"
                  disabled={codeLoading}
                >
                  {codeLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      Vérifier le code
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </form>
            ) : (
              <form onSubmit={handleActivateWithCode} className="space-y-4">
                <div className="bg-green-900/30 border border-green-700 rounded-lg p-3 mb-4">
                  <p className="text-green-300 text-sm flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Code valide - {codeInfo?.available_licenses} licence(s) disponible(s)
                  </p>
                  {codeInfo?.order_number && (
                    <p className="text-green-400/70 text-xs mt-1">
                      Commande #{codeInfo.order_number} • Plan: {codeInfo.plan}
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="code-email" className="text-gray-300">Votre nom d'utilisateur</Label>
                  <Input
                    id="code-email"
                    type="text"
                    value={codeEmail}
                    onChange={(e) => setCodeEmail(e.target.value)}
                    className="bg-gray-900/50 border-gray-700 text-white"
                    placeholder="Choisissez un nom d'utilisateur"
                    disabled={codeLoading}
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    Ce nom sera votre identifiant de connexion
                  </p>
                </div>

                <div>
                  <Label htmlFor="code-password" className="text-gray-300">Nouveau mot de passe</Label>
                  <Input
                    id="code-password"
                    type="password"
                    value={codePassword}
                    onChange={(e) => setCodePassword(e.target.value)}
                    className="bg-gray-900/50 border-gray-700 text-white"
                    placeholder="Minimum 6 caractères"
                    disabled={codeLoading}
                  />
                </div>

                <div>
                  <Label htmlFor="code-password-confirm" className="text-gray-300">Confirmer le mot de passe</Label>
                  <Input
                    id="code-password-confirm"
                    type="password"
                    value={codePasswordConfirm}
                    onChange={(e) => setCodePasswordConfirm(e.target.value)}
                    className="bg-gray-900/50 border-gray-700 text-white"
                    placeholder="Confirmez votre mot de passe"
                    disabled={codeLoading}
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-amber-600 hover:bg-amber-700 h-12"
                  disabled={codeLoading}
                >
                  {codeLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Créer mon compte
                    </>
                  )}
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Activation Success Screen
  if (activationSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 pt-24 bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950">
        <div className="w-full max-w-md">
          <div className="glass rounded-2xl p-8 fade-in border border-gray-800 text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold mb-2 text-white">Compte activé!</h1>
            <p className="text-gray-400 mb-6">Votre compte a été créé avec succès.</p>
            
            <div className="bg-gray-800/50 rounded-lg p-4 mb-4 text-left">
              <p className="text-gray-300 text-sm mb-2">
                <span className="text-gray-500">Nom d'utilisateur:</span>{' '}
                <span className="font-mono text-amber-500 font-bold text-base">{activationSuccess.username}</span>
              </p>
              <p className="text-gray-300 text-sm">
                <span className="text-gray-500">Plan:</span>{' '}
                <span className="text-green-400 capitalize">{activationSuccess.plan}</span>
              </p>
            </div>

            <div className="bg-blue-900/30 border border-blue-700/50 rounded-lg p-3 mb-6 text-left">
              <p className="text-blue-300 text-xs">
                <strong>⚠️ Important:</strong> Pour vous connecter, utilisez votre <strong>nom d'utilisateur</strong> ci-dessus et le mot de passe que vous avez choisi.
              </p>
            </div>
            
            <Button
              onClick={() => {
                setUsername(activationSuccess.username);
                resetActivation();
              }}
              className="w-full bg-amber-600 hover:bg-amber-700"
            >
              <LogIn className="w-4 h-4 mr-2" />
              Se connecter maintenant
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Activation Form
  if (showActivation) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 pt-24 bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950">
        <div className="w-full max-w-md">
          <Button 
            variant="ghost" 
            onClick={resetActivation}
            className="text-gray-400 hover:text-white mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour à la connexion
          </Button>

          <div className="glass rounded-2xl p-8 fade-in border border-gray-800">
            <div className="text-center mb-8">
              <UserPlus className="w-12 h-12 text-amber-500 mx-auto mb-4" />
              <h1 className="text-2xl font-bold mb-2 text-white">
                Activer mon compte
              </h1>
              <p className="text-gray-400">
                {activationStep === 1 
                  ? "Entrez l'email utilisé lors de votre achat Shopify"
                  : "Créez votre mot de passe"
                }
              </p>
            </div>

            {/* Progress Steps */}
            <div className="flex items-center justify-center gap-2 mb-6">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                activationStep >= 1 ? 'bg-amber-600 text-white' : 'bg-gray-700 text-gray-400'
              }`}>1</div>
              <div className={`w-12 h-1 ${activationStep >= 2 ? 'bg-amber-600' : 'bg-gray-700'}`}></div>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                activationStep >= 2 ? 'bg-amber-600 text-white' : 'bg-gray-700 text-gray-400'
              }`}>2</div>
            </div>

            {activationStep === 1 ? (
              <form onSubmit={handleCheckEmail} className="space-y-4">
                <div>
                  <Label htmlFor="activation-email" className="text-gray-300">Email Shopify</Label>
                  <Input
                    id="activation-email"
                    type="email"
                    value={activationEmail}
                    onChange={(e) => setActivationEmail(e.target.value)}
                    className="bg-gray-900/50 border-gray-700 text-white"
                    placeholder="votre@email.com"
                    disabled={activationLoading}
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-amber-600 hover:bg-amber-700 h-12"
                  disabled={activationLoading}
                >
                  {activationLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      Vérifier mon abonnement
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </form>
            ) : (
              <form onSubmit={handleActivateAccount} className="space-y-4">
                <div className="bg-green-900/30 border border-green-700 rounded-lg p-3 mb-4">
                  <p className="text-green-300 text-sm flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Abonnement vérifié pour {activationEmail}
                  </p>
                </div>

                <div>
                  <Label htmlFor="activation-password" className="text-gray-300">Nouveau mot de passe</Label>
                  <Input
                    id="activation-password"
                    type="password"
                    value={activationPassword}
                    onChange={(e) => setActivationPassword(e.target.value)}
                    className="bg-gray-900/50 border-gray-700 text-white"
                    placeholder="Minimum 6 caractères"
                    disabled={activationLoading}
                  />
                </div>

                <div>
                  <Label htmlFor="activation-password-confirm" className="text-gray-300">Confirmer le mot de passe</Label>
                  <Input
                    id="activation-password-confirm"
                    type="password"
                    value={activationPasswordConfirm}
                    onChange={(e) => setActivationPasswordConfirm(e.target.value)}
                    className="bg-gray-900/50 border-gray-700 text-white"
                    placeholder="Confirmez votre mot de passe"
                    disabled={activationLoading}
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-amber-600 hover:bg-amber-700 h-12"
                  disabled={activationLoading}
                >
                  {activationLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Activer mon compte
                    </>
                  )}
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Normal Login Form
  return (
    <div className="min-h-screen flex items-center justify-center p-6 pt-24 bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950">
      <div className="w-full max-w-md">
        {/* Back to landing */}
        <Button 
          variant="ghost" 
          onClick={() => navigate("/")}
          className="text-gray-400 hover:text-white mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour à l'accueil
        </Button>

        <div className="glass rounded-2xl p-8 fade-in border border-gray-800">
          <div className="text-center mb-8">
            <img 
              src="/logo-sne.png" 
              alt="SNE Logo" 
              className="h-20 w-auto mx-auto mb-4"
            />
            <h1 className="text-2xl font-bold mb-2 text-white">
              Connexion
            </h1>
            <p className="text-gray-400">Accédez à votre espace OSINT</p>
          </div>

          {/* Subscription Error Message */}
          {subscriptionError && (
            <div className="mb-6 p-4 bg-red-900/30 border border-red-700 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-red-300 font-medium">Abonnement requis</p>
                  <p className="text-red-400 text-sm mt-1">{subscriptionError.reason}</p>
                  {subscriptionError.plan === 'demo_expired' && (
                    <p className="text-red-400 text-sm mt-1">Votre période d'essai de 3 jours est terminée.</p>
                  )}
                  <Button
                    onClick={handleSubscribe}
                    className="mt-3 bg-amber-600 hover:bg-amber-700 text-white"
                    size="sm"
                  >
                    <CreditCard className="w-4 h-4 mr-2" />
                    Voir les plans d'abonnement
                  </Button>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="username" className="text-gray-300">Email ou nom d'utilisateur</Label>
              <Input
                data-testid="username-input"
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="bg-gray-900/50 border-gray-700 text-white"
                placeholder="votre@email.com"
                disabled={loading}
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-gray-300">Mot de passe</Label>
              <Input
                data-testid="password-input"
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-gray-900/50 border-gray-700 text-white"
                placeholder="Entrez votre mot de passe"
                disabled={loading}
              />
            </div>

            <Button
              data-testid="login-btn"
              type="submit"
              className="w-full bg-amber-600 hover:bg-amber-700 h-12 text-lg"
              disabled={loading}
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <LogIn className="w-5 h-5 mr-2" />
                  Se connecter
                </>
              )}
            </Button>
          </form>

          {/* Activation Section */}
          <div className="mt-6 space-y-3">
            <div className="p-4 bg-amber-900/20 border border-amber-700/50 rounded-lg">
              <p className="text-amber-300 text-sm font-medium flex items-center gap-2 mb-2">
                <Key className="w-4 h-4" />
                Activer mon abonnement
              </p>
              <Button
                variant="outline"
                className="w-full border-amber-600 text-amber-400 hover:bg-amber-600/10"
                onClick={() => setShowCodeActivation(true)}
              >
                <Key className="w-4 h-4 mr-2" />
                Code d'activation
              </Button>
            </div>
          </div>

          <div className="mt-6 space-y-3">
            <div className="text-center text-sm text-gray-400">
              <p>Mot de passe oublié ?</p>
              <p className="mt-1">Contactez <span className="text-amber-500">infoosintquebec@gmail.com</span></p>
            </div>

            <div className="border-t border-gray-800 pt-4">
              <p className="text-center text-sm text-gray-400 mb-3">Pas encore abonné ?</p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1 border-amber-600 text-amber-500 hover:bg-amber-600/10"
                  onClick={() => window.open('https://solutions-numeriques-enquetes.com/collections/all', '_blank')}
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  S'abonner
                </Button>
                <Button
                  variant="outline"
                  className="flex-1 border-gray-600 text-gray-300 hover:bg-gray-800"
                  onClick={() => navigate("/")}
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Aperçu
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
